import React, { useState, useRef, useEffect } from 'react';

const GridOverlay = ({ containerRef, enabled = false, gridSize = 3, onHighlightSquare = null }) => {
  const [highlightedSquare, setHighlightedSquare] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, left: 0, top: 0 });
  const gridRef = useRef(null);

  useEffect(() => {
    if (!enabled || !containerRef?.current) {
      setDimensions({ width: 0, height: 0, left: 0, top: 0 });
      return;
    }

    const updateDimensions = () => {
      const container = containerRef.current;
      if (container) {
        // Find the actual image element within the container
        const img = container.querySelector('img');
        if (img) {
          const containerRect = container.getBoundingClientRect();
          const imgRect = img.getBoundingClientRect();
          
          // Calculate position relative to container
          const left = imgRect.left - containerRect.left;
          const top = imgRect.top - containerRect.top;
          
          setDimensions({ 
            width: imgRect.width, 
            height: imgRect.height,
            left: left,
            top: top
          });
        } else {
          // Fallback to container if no image found
          const rect = container.getBoundingClientRect();
          setDimensions({ 
            width: rect.width, 
            height: rect.height,
            left: 0,
            top: 0
          });
        }
      }
    };

    updateDimensions();
    
    // Use multiple strategies to detect image load
    const container = containerRef.current;
    const img = container?.querySelector('img');
    
    if (img) {
      if (img.complete) {
        updateDimensions();
      } else {
        img.addEventListener('load', updateDimensions);
        img.addEventListener('load', updateDimensions, { once: true });
      }
    }
    
    window.addEventListener('resize', updateDimensions);
    
    // Use MutationObserver to detect when image loads and container size changes
    const observer = new MutationObserver(updateDimensions);
    if (container) {
      observer.observe(container, { 
        childList: true, 
        subtree: true, 
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    // Also observe the image if it exists
    if (img) {
      observer.observe(img, {
        attributes: true,
        attributeFilter: ['src', 'style', 'class']
      });
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      if (img) {
        img.removeEventListener('load', updateDimensions);
      }
      observer.disconnect();
    };
  }, [enabled, containerRef]);

  if (!enabled || !containerRef?.current || dimensions.width === 0 || dimensions.height === 0) {
    return null;
  }

  const handleSquareClick = (row, col) => {
    const newHighlight = highlightedSquare?.row === row && highlightedSquare?.col === col ? null : { row, col };
    setHighlightedSquare(newHighlight);
    if (onHighlightSquare) {
      onHighlightSquare(newHighlight);
    }
  };

  return (
    <div
      ref={gridRef}
      className="absolute pointer-events-none"
      style={{
        left: `${dimensions.left}px`,
        top: `${dimensions.top}px`,
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        zIndex: 10,
        pointerEvents: 'auto'
      }}
    >
      {/* Grid Lines */}
      {Array.from({ length: gridSize + 1 }).map((_, i) => (
        <React.Fragment key={`vertical-${i}`}>
          {/* Vertical Lines - Black for visibility */}
          <div
            className="absolute"
            style={{
              left: `${(i / gridSize) * 100}%`,
              top: 0,
              width: '2px',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)', // Black for visibility
              boxShadow: '0 0 2px rgba(0, 0, 0, 0.5)',
              pointerEvents: 'none'
            }}
          />
          {/* Horizontal Lines - Black for visibility */}
          <div
            className="absolute"
            style={{
              top: `${(i / gridSize) * 100}%`,
              left: 0,
              height: '2px',
              width: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)', // Black for visibility
              boxShadow: '0 0 2px rgba(0, 0, 0, 0.5)',
              pointerEvents: 'none'
            }}
          />
        </React.Fragment>
      ))}

      {/* Grid Squares - Clickable for Highlight */}
      {Array.from({ length: gridSize }).map((_, row) =>
        Array.from({ length: gridSize }).map((_, col) => {
          const isHighlighted = highlightedSquare?.row === row && highlightedSquare?.col === col;
          const isDimmed = highlightedSquare && !isHighlighted;

          return (
            <div
              key={`square-${row}-${col}`}
              onClick={() => handleSquareClick(row, col)}
              className="absolute cursor-pointer transition-all duration-300"
              style={{
                left: `${(col / gridSize) * 100}%`,
                top: `${(row / gridSize) * 100}%`,
                width: `${100 / gridSize}%`,
                height: `${100 / gridSize}%`,
                backgroundColor: isHighlighted 
                  ? 'rgba(0, 0, 0, 0.2)' // Black highlight
                  : isDimmed 
                    ? 'rgba(0, 0, 0, 0.1)' // Dimmed
                    : 'transparent',
                border: isHighlighted ? '3px solid rgba(0, 0, 0, 0.9)' : 'none',
                boxShadow: isHighlighted ? '0 0 8px rgba(0, 0, 0, 0.6)' : 'none',
                borderRadius: isHighlighted ? '4px' : '0',
                pointerEvents: 'auto'
              }}
              title={`Grid Square ${row + 1},${col + 1}`}
            />
          );
        })
      )}
    </div>
  );
};

export default GridOverlay;

